import { fromChainId, toAssetId } from '@shapeshiftoss/caip';
import { supportsArbitrum, supportsArbitrumNova, supportsAvalanche, supportsBase, supportsBSC, supportsETH, supportsGnosis, supportsOptimism, supportsPolygon, } from '@shapeshiftoss/hdwallet-core';
import { KnownChainIds } from '@shapeshiftoss/types';
import { isSome } from '@shapeshiftoss/utils';
import BigNumber from 'bignumber.js';
import PQueue from 'p-queue';
import { isAddress, toHex } from 'viem';
import { ChainAdapterError, ErrorHandler } from '../error/ErrorHandler';
import { CONTRACT_INTERACTION, ValidAddressResultType } from '../types';
import { getAssetNamespace, toAddressNList, toRootDerivationPath, verifyLedgerAppOpen, } from '../utils';
import { bnOrZero } from '../utils/bignumber';
import { assertAddressNotSanctioned } from '../utils/validateAddress';
import { getErc20Data } from './utils';
export const evmChainIds = [
    KnownChainIds.EthereumMainnet,
    KnownChainIds.AvalancheMainnet,
    KnownChainIds.OptimismMainnet,
    KnownChainIds.BnbSmartChainMainnet,
    KnownChainIds.PolygonMainnet,
    KnownChainIds.GnosisMainnet,
    KnownChainIds.ArbitrumMainnet,
    KnownChainIds.ArbitrumNovaMainnet,
    KnownChainIds.BaseMainnet,
];
export const isEvmChainId = (maybeEvmChainId) => {
    return evmChainIds.includes(maybeEvmChainId);
};
export class EvmBaseAdapter {
    chainId;
    rootBip44Params;
    supportedChainIds;
    providers;
    rpcUrl;
    assetId;
    parser;
    constructor(args) {
        this.assetId = args.assetId;
        this.chainId = args.chainId;
        this.rootBip44Params = args.rootBip44Params;
        this.parser = args.parser;
        this.providers = args.providers;
        this.rpcUrl = args.rpcUrl;
        this.supportedChainIds = args.supportedChainIds;
        if (!this.supportedChainIds.includes(this.chainId)) {
            throw new Error(`${this.chainId} not supported. (supported: ${this.supportedChainIds})`);
        }
    }
    getChainId() {
        return this.chainId;
    }
    getRpcUrl() {
        return this.rpcUrl;
    }
    getBip44Params({ accountNumber }) {
        if (accountNumber < 0)
            throw new Error('accountNumber must be >= 0');
        return { ...this.rootBip44Params, accountNumber, isChange: false, addressIndex: 0 };
    }
    assertSupportsChain(wallet, chainReference) {
        const support = (() => {
            switch (chainReference ?? Number(fromChainId(this.chainId).chainReference)) {
                case Number(fromChainId(KnownChainIds.AvalancheMainnet).chainReference):
                    return supportsAvalanche(wallet);
                case Number(fromChainId(KnownChainIds.BnbSmartChainMainnet).chainReference):
                    return supportsBSC(wallet);
                case Number(fromChainId(KnownChainIds.EthereumMainnet).chainReference):
                    return supportsETH(wallet);
                case Number(fromChainId(KnownChainIds.OptimismMainnet).chainReference):
                    return supportsOptimism(wallet);
                case Number(fromChainId(KnownChainIds.PolygonMainnet).chainReference):
                    return supportsPolygon(wallet);
                case Number(fromChainId(KnownChainIds.GnosisMainnet).chainReference):
                    return supportsGnosis(wallet);
                case Number(fromChainId(KnownChainIds.ArbitrumMainnet).chainReference):
                    return supportsArbitrum(wallet);
                case Number(fromChainId(KnownChainIds.ArbitrumNovaMainnet).chainReference):
                    return supportsArbitrumNova(wallet);
                case Number(fromChainId(KnownChainIds.BaseMainnet).chainReference):
                    return supportsBase(wallet);
                default:
                    return false;
            }
        })();
        if (!support) {
            throw new ChainAdapterError(`wallet does not support: ${this.getDisplayName()}`, {
                translation: 'chainAdapters.errors.unsupportedChain',
                options: { chain: this.getDisplayName() },
            });
        }
    }
    async assertSwitchChain(wallet) {
        if (!wallet.ethGetChainId)
            return;
        const walletChainReference = await wallet.ethGetChainId();
        const adapterChainReference = Number(fromChainId(this.chainId).chainReference);
        // switch chain not needed if wallet and adapter chains match
        if (walletChainReference === adapterChainReference)
            return;
        // error if wallet and adapter chains don't match, but switch chain isn't supported by the wallet
        if (!wallet.ethSwitchChain) {
            throw new ChainAdapterError(`wallet does not support switching chains: wallet network (${walletChainReference}) and adapter network (${adapterChainReference}) do not match.`, { translation: 'chainAdapters.errors.switchChainUnsupported' });
        }
        // TODO: use asset-service baseAssets.ts after lib is moved into web (circular dependency)
        const targetNetwork = {
            [KnownChainIds.AvalancheMainnet]: {
                name: 'Avalanche',
                symbol: 'AVAX',
                explorer: 'https://snowtrace.dev',
            },
            [KnownChainIds.BnbSmartChainMainnet]: {
                name: 'BNB',
                symbol: 'BNB',
                explorer: 'https://bscscan.com',
            },
            [KnownChainIds.PolygonMainnet]: {
                name: 'Polygon Ecosystem Token',
                symbol: 'POL',
                explorer: 'https://polygonscan.com/',
            },
            [KnownChainIds.GnosisMainnet]: {
                name: 'xDAI',
                symbol: 'xDAI',
                explorer: 'https://gnosis.blockscout.com',
            },
            [KnownChainIds.EthereumMainnet]: {
                name: 'Ethereum',
                symbol: 'ETH',
                explorer: 'https://etherscan.io',
            },
            [KnownChainIds.OptimismMainnet]: {
                name: 'Ethereum',
                symbol: 'ETH',
                explorer: 'https://optimistic.etherscan.io',
            },
            [KnownChainIds.ArbitrumMainnet]: {
                name: 'Ethereum',
                symbol: 'ETH',
                explorer: 'https://arbiscan.io',
            },
            [KnownChainIds.ArbitrumNovaMainnet]: {
                name: 'Ethereum',
                symbol: 'ETH',
                explorer: 'https://nova.arbiscan.io',
            },
            [KnownChainIds.BaseMainnet]: {
                name: 'Ethereum',
                symbol: 'ETH',
                explorer: 'https://basescan.org',
            },
        }[this.chainId];
        try {
            await wallet.ethSwitchChain({
                chainId: toHex(adapterChainReference),
                chainName: this.getDisplayName(),
                nativeCurrency: {
                    name: targetNetwork.name,
                    symbol: targetNetwork.symbol,
                    decimals: 18,
                },
                rpcUrls: [this.getRpcUrl()],
                blockExplorerUrls: [targetNetwork.explorer],
            });
        }
        catch (err) {
            // Internal MM bug when broadcasting the wallet_addEthereumChain JSON-RPC call
            // This is *not* an exception, it actually succeeds
            // See https://github.com/shapeshift/web/issues/9301#issuecomment-2790459115
            if (err.code === -32603 && err.message.includes('is not a function'))
                return;
            throw new ChainAdapterError(err, {
                translation: 'chainAdapters.errors.switchChainFailed',
                options: { chain: this.getDisplayName() },
            });
        }
    }
    async buildSendApiTransaction(input) {
        try {
            const { to, from, value, accountNumber, chainSpecific, customNonce } = input;
            const { data, contractAddress, gasPrice, gasLimit, maxFeePerGas, maxPriorityFeePerGas } = chainSpecific;
            if (!to)
                throw new Error('to is required');
            if (!value)
                throw new Error('value is required');
            if (!gasLimit)
                throw new Error('gasLimit is required');
            const account = await this.getAccount(from);
            const isTokenSend = !!contractAddress;
            const fees = (() => {
                if (maxFeePerGas && maxPriorityFeePerGas) {
                    return {
                        maxFeePerGas: toHex(BigInt(maxFeePerGas)),
                        maxPriorityFeePerGas: toHex(BigInt(maxPriorityFeePerGas)),
                    };
                }
                if (!gasPrice)
                    throw new Error('gasPrice is required for non EIP-1559 fees');
                return { gasPrice: toHex(BigInt(gasPrice)) };
            })();
            const nonce = customNonce !== undefined
                ? toHex(BigInt(customNonce))
                : toHex(BigInt(account.chainSpecific.nonce));
            const txToSign = {
                addressNList: toAddressNList(this.getBip44Params({ accountNumber })),
                value: toHex(isTokenSend ? 0n : BigInt(value)),
                to: isTokenSend ? contractAddress : to,
                chainId: Number(fromChainId(this.chainId).chainReference),
                data: data || getErc20Data(to, value, contractAddress) || '0x',
                nonce,
                gasLimit: toHex(BigInt(gasLimit)),
                ...fees,
            };
            return txToSign;
        }
        catch (err) {
            return ErrorHandler(err, {
                translation: 'chainAdapters.errors.buildTransaction',
            });
        }
    }
    async buildSendTransaction(input) {
        try {
            this.assertSupportsChain(input.wallet);
            const from = await this.getAddress(input);
            const txToSign = await this.buildSendApiTransaction({ ...input, from });
            return { txToSign };
        }
        catch (err) {
            return ErrorHandler(err, {
                translation: 'chainAdapters.errors.buildTransaction',
            });
        }
    }
    buildEstimateGasBody({ to, value, chainSpecific: { contractAddress, from, data }, }) {
        const isTokenSend = !!contractAddress;
        return {
            from,
            to: isTokenSend ? contractAddress : to,
            value: isTokenSend ? '0' : value,
            data: data || getErc20Data(to, value, contractAddress),
        };
    }
    async getAccount(pubkey) {
        try {
            const data = await this.providers.http.getAccount({ pubkey });
            const balance = bnOrZero(data.balance).plus(bnOrZero(data.unconfirmedBalance));
            return {
                balance: balance.toString(),
                chainId: this.chainId,
                assetId: this.assetId,
                chain: this.getType(),
                chainSpecific: {
                    nonce: data.nonce,
                    tokens: data.tokens.map(token => ({
                        balance: token.balance,
                        assetId: toAssetId({
                            chainId: this.chainId,
                            assetNamespace: getAssetNamespace(token.type),
                            assetReference: token.id ? `${token.contract}/${token.id}` : token.contract,
                        }),
                        name: token.name,
                        precision: token.decimals,
                        symbol: token.symbol,
                    })),
                },
                pubkey,
            };
        }
        catch (err) {
            return ErrorHandler(err, {
                translation: 'chainAdapters.errors.getAccount',
                options: { pubkey },
            });
        }
    }
    async getTxHistory(input) {
        const requestQueue = input.requestQueue ?? new PQueue();
        const data = await requestQueue.add(() => this.providers.http.getTxHistory({
            pubkey: input.pubkey,
            pageSize: input.pageSize,
            cursor: input.cursor,
        }));
        const transactions = await Promise.all(data.txs.map(tx => {
            if (input.knownTxIds?.has(tx.txid)) {
                return null;
            }
            return requestQueue.add(() => this.parseTx(tx, input.pubkey));
        }));
        const filteredTxs = transactions.filter(isSome);
        return {
            cursor: data.cursor ?? '',
            pubkey: input.pubkey,
            transactions: filteredTxs,
        };
    }
    async signTransaction(signTxInput) {
        try {
            const { txToSign, wallet } = signTxInput;
            this.assertSupportsChain(wallet, txToSign.chainId);
            await verifyLedgerAppOpen(this.chainId, wallet);
            const signedTx = await wallet.ethSignTx(txToSign);
            if (!signedTx?.serialized)
                throw new Error('error signing tx');
            return signedTx.serialized;
        }
        catch (err) {
            return ErrorHandler(err, {
                translation: 'chainAdapters.errors.signTransaction',
            });
        }
    }
    async signAndBroadcastTransaction({ senderAddress, receiverAddress, signTxInput, }) {
        try {
            const { txToSign, wallet } = signTxInput;
            await Promise.all([
                assertAddressNotSanctioned(senderAddress),
                receiverAddress !== CONTRACT_INTERACTION && assertAddressNotSanctioned(receiverAddress),
            ]);
            this.assertSupportsChain(wallet, txToSign.chainId);
            await this.assertSwitchChain(wallet);
            const txHash = await wallet.ethSendTx?.(txToSign);
            if (!txHash)
                throw new Error('error signing & broadcasting tx');
            return txHash.hash;
        }
        catch (err) {
            return ErrorHandler(err, {
                translation: 'chainAdapters.errors.signAndBroadcastTransaction',
            });
        }
    }
    async broadcastTransaction({ senderAddress, receiverAddress, hex, }) {
        try {
            await Promise.all([
                assertAddressNotSanctioned(senderAddress),
                receiverAddress !== CONTRACT_INTERACTION && assertAddressNotSanctioned(receiverAddress),
            ]);
            const txHash = await this.providers.http.sendTx({ sendTxBody: { hex } });
            return txHash;
        }
        catch (err) {
            if (err.name === 'ResponseError') {
                const response = await err.response.json();
                const error = JSON.parse(response.message);
                return ErrorHandler(JSON.stringify(response), {
                    translation: 'chainAdapters.errors.broadcastTransactionWithMessage',
                    options: { message: error.message },
                });
            }
            return ErrorHandler(err, {
                translation: 'chainAdapters.errors.broadcastTransaction',
            });
        }
    }
    async signMessage(signMessageInput) {
        try {
            const { messageToSign, wallet } = signMessageInput;
            this.assertSupportsChain(wallet);
            await this.assertSwitchChain(wallet);
            await verifyLedgerAppOpen(this.chainId, wallet);
            const signedMessage = await wallet.ethSignMessage(messageToSign);
            if (!signedMessage)
                throw new Error('error signing message');
            return signedMessage.signature;
        }
        catch (err) {
            return ErrorHandler(err, {
                translation: 'chainAdapters.errors.signMessage',
            });
        }
    }
    async signTypedData(input) {
        try {
            const { typedDataToSign, wallet } = input;
            this.assertSupportsChain(wallet);
            if (!wallet.ethSignTypedData)
                throw new Error('wallet does not support signing typed data');
            await this.assertSwitchChain(wallet);
            await verifyLedgerAppOpen(this.chainId, wallet);
            const result = await wallet.ethSignTypedData(typedDataToSign);
            if (!result)
                throw new Error('error signing typed data');
            return result.signature;
        }
        catch (err) {
            return ErrorHandler(err, {
                translation: 'chainAdapters.errors.signMessage',
            });
        }
    }
    async getAddress(input) {
        try {
            const { accountNumber, wallet, showOnDevice = false } = input;
            if (input.pubKey)
                return input.pubKey;
            this.assertSupportsChain(wallet);
            await verifyLedgerAppOpen(this.chainId, wallet);
            const bip44Params = this.getBip44Params({ accountNumber });
            const address = await wallet.ethGetAddress({
                addressNList: toAddressNList(bip44Params),
                showDisplay: showOnDevice,
            });
            if (!address)
                throw new Error('error getting address from wallet');
            return address;
        }
        catch (err) {
            return ErrorHandler(err, {
                translation: 'chainAdapters.errors.getAddress',
            });
        }
    }
    // eslint-disable-next-line require-await
    async validateAddress(address) {
        const isValidAddress = isAddress(address);
        if (isValidAddress)
            return { valid: true, result: ValidAddressResultType.Valid };
        return { valid: false, result: ValidAddressResultType.Invalid };
    }
    async subscribeTxs(input, onMessage, onError) {
        const { pubKey, accountNumber, wallet } = input;
        const address = await this.getAddress({ accountNumber, wallet, pubKey });
        const bip44Params = this.getBip44Params({ accountNumber });
        const subscriptionId = toRootDerivationPath(bip44Params);
        await this.providers.ws.subscribeTxs(subscriptionId, { topic: 'txs', addresses: [address] }, async (msg) => onMessage(await this.parseTx(msg.data, msg.address)), err => onError({ message: err.message }));
    }
    unsubscribeTxs(input) {
        if (!input)
            return this.providers.ws.unsubscribeTxs();
        const { accountNumber } = input;
        const bip44Params = this.getBip44Params({ accountNumber });
        const subscriptionId = toRootDerivationPath(bip44Params);
        this.providers.ws.unsubscribeTxs(subscriptionId, { topic: 'txs', addresses: [] });
    }
    closeTxs() {
        this.providers.ws.close('txs');
    }
    async buildCustomApiTx(input) {
        try {
            const { to, from, accountNumber, data, value } = input;
            const { gasPrice, gasLimit, maxFeePerGas, maxPriorityFeePerGas } = input;
            const account = await this.getAccount(from);
            const fees = maxFeePerGas && maxPriorityFeePerGas
                ? {
                    maxFeePerGas: toHex(BigInt(maxFeePerGas)),
                    maxPriorityFeePerGas: toHex(BigInt(maxPriorityFeePerGas)),
                }
                : { gasPrice: toHex(gasPrice ? BigInt(gasPrice) : 0n) };
            const bip44Params = this.getBip44Params({ accountNumber });
            const txToSign = {
                addressNList: toAddressNList(bip44Params),
                value: toHex(BigInt(value)),
                to,
                chainId: Number(fromChainId(this.chainId).chainReference),
                data,
                nonce: toHex(BigInt(account.chainSpecific.nonce)),
                gasLimit: toHex(BigInt(gasLimit)),
                ...fees,
            };
            return txToSign;
        }
        catch (err) {
            return ErrorHandler(err, {
                translation: 'chainAdapters.errors.buildTransaction',
            });
        }
    }
    async buildCustomTx(input) {
        try {
            const { wallet, accountNumber } = input;
            this.assertSupportsChain(wallet);
            const from = await this.getAddress({ accountNumber, wallet });
            const txToSign = await this.buildCustomApiTx({ ...input, from });
            return { txToSign };
        }
        catch (err) {
            return ErrorHandler(err, {
                translation: 'chainAdapters.errors.buildTransaction',
            });
        }
    }
    async getGasFeeData() {
        try {
            const { fast, average, slow } = await this.providers.http.getGasFees();
            return { fast, average, slow };
        }
        catch (err) {
            return ErrorHandler(err, {
                translation: 'chainAdapters.errors.getGasFeeData',
            });
        }
    }
    async getFeeData(input) {
        try {
            const { gasLimit } = await this.providers.http.estimateGas({
                estimateGasBody: this.buildEstimateGasBody(input),
            });
            const { fast, average, slow } = await this.getGasFeeData();
            return {
                fast: {
                    txFee: bnOrZero(BigNumber.max(fast.gasPrice, fast.maxFeePerGas ?? 0).times(gasLimit)).toFixed(0),
                    chainSpecific: { gasLimit, ...fast },
                },
                average: {
                    txFee: bnOrZero(BigNumber.max(average.gasPrice, average.maxFeePerGas ?? 0).times(gasLimit)).toFixed(0),
                    chainSpecific: { gasLimit, ...average },
                },
                slow: {
                    txFee: bnOrZero(BigNumber.max(slow.gasPrice, slow.maxFeePerGas ?? 0).times(gasLimit)).toFixed(0),
                    chainSpecific: { gasLimit, ...slow },
                },
            };
        }
        catch (err) {
            return ErrorHandler(err, {
                translation: 'chainAdapters.errors.getFeeData',
            });
        }
    }
    async parseTx(tx, pubkey) {
        const { address: _, ...parsedTx } = await this.parser.parse(tx, pubkey);
        return {
            ...parsedTx,
            pubkey,
            transfers: parsedTx.transfers.map(transfer => ({
                assetId: transfer.assetId,
                from: [transfer.from],
                to: [transfer.to],
                type: transfer.type,
                value: transfer.totalValue,
                id: transfer.id,
                token: transfer.token,
            })),
        };
    }
    get httpProvider() {
        return this.providers.http;
    }
    get wsProvider() {
        return this.providers.ws;
    }
}
